import { Injectable } from '@angular/core';
import { Token, Secret } from '@crmackey/fernet';
import CryptoJS from 'crypto-js';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CryptedService {
  private readonly dataSecretKey: string;
  private readonly signatureSecretKey: string;
  private fernetSecret: Secret;

  constructor() {
    this.dataSecretKey = this.getConfigValue('DATA_SECRET');
    this.signatureSecretKey = this.getConfigValue('SIGNATURE_SECRET');
    this.initFernet();
  }

  private getConfigValue(key: string): string {
    const config: Record<string, string> = {
      'DATA_SECRET': environment.dataSecretKey,
      'SIGNATURE_SECRET': environment.signatureSecretKey,
    };
    return config[key];
  }

  private initFernet(): void {
    // Initialize fernet with our data secret
    this.fernetSecret = new Secret(this.dataSecretKey);
  }

  /**
   * Computes HMAC signature for data using our signature secret
   */
  private computeHmac(data: any): string {
    // This string needs to match the python binary string used exactly
    const jsonStr = (typeof data === 'string') ? data.replace(/: /g, ":").replace(/, /g, ",") : JSON.stringify(data);
    // Using CryptoJS for HMAC computation
    return CryptoJS.HmacSHA256(jsonStr, this.signatureSecretKey).toString(CryptoJS.enc.Hex);
  }

  /**
   * Encrypts data and returns both the encrypted content and HMAC
   */
  public encryptData(data: any): { encrypted: string, hmac: string } {
    // Compute HMAC of the original data
    const hmac = this.computeHmac(data);

    // Encrypt the data using fernet
    const jsonStr = JSON.stringify(data);
    const token = new Token({ secret: this.fernetSecret });
    const encrypted = token.encode(jsonStr);

    return { encrypted, hmac };
  }

  /**
   * Decrypts data and optionally verifies the HMAC
   */
  public decryptData(encryptedStr: string, hmac?: string, hmac_data?: Record<string, any>): any {
    try {
      // Create a token for decryption with TTL=0 (no expiration check)
      const token = new Token({
        secret: this.fernetSecret,
        token: encryptedStr,
        ttl: 0
      });

      // Decrypt the data
      const decrypted = token.decode();
      const parsedData = JSON.parse(decrypted);

      // If HMAC is provided, verify it
      if (hmac) {
        const computedHmac = this.computeHmac(hmac_data ? JSON.stringify(hmac_data) : decrypted);
        if (computedHmac !== hmac) {
          throw new Error('HMAC verification failed');
        }
      }

      return parsedData;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Decryption failed: ${error.message}`);
      }
      throw new Error('Decryption failed');
    }
  }
}